import {RefObject, useEffect, useState} from 'react';

import {TextInputRef} from '../../../components/ui-controls/form/TextInput';

export const PASSWORDS_DO_NOT_MATCH = 'Passwords do not match';

/**
 * DIS-1862: when the "Passwords do not match" line shows on a Confirm
 * password field.
 *
 * - Typing in Confirm password never shows it.
 * - Leaving Confirm password with a mismatch shows it.
 * - Once shown, it clears as soon as the two fields match.
 * - A submit with a mismatch shows it and focuses Confirm password.
 *
 * Blur never moves focus back to the field: a forced return on blur traps
 * keyboard users on web (WCAG 3.2.1). The state is local, not an RHF error,
 * so it never touches the form's `isValid`.
 */
export function useConfirmPasswordMismatch(
  password: string | undefined,
  confirmPassword: string | undefined,
  confirmPasswordRef: RefObject<TextInputRef | null>,
) {
  const [shown, setShown] = useState(false);
  const mismatch = !!confirmPassword && confirmPassword !== (password ?? '');

  useEffect(() => {
    if (!mismatch) setShown(false);
  }, [mismatch]);

  return {
    /** The two fields hold different non-empty values. */
    mismatch,
    /** Render the "Passwords do not match" line. */
    showMismatch: shown && mismatch,
    /** Call from Confirm password's onBlur. */
    onConfirmBlur: () => {
      if (mismatch) setShown(true);
    },
    /** Call from handleSubmit's onInvalid callback. */
    onInvalidSubmit: () => {
      if (!mismatch) return;
      setShown(true);
      confirmPasswordRef.current?.focus();
    },
  };
}
